/**
 * Dashboard — list of the writer's scripts with search, sort, duplicate and
 * a name-on-create prompt.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { AppHeader } from "@/components/Header";
import { TrialBanner } from "@/components/TrialBanner";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { pageCountLabel } from "@/lib/pageCount";
import { Plus, FileText, Copy, Trash2, Search, ChevronDown, FilePlus2 } from "lucide-react";
import { toast } from "sonner";

const SORTS = [
  { id: "modified", label: "Date modified" },
  { id: "created", label: "Date created" },
  { id: "name", label: "Name" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [scripts, setScripts] = useState([]);
  const [shared, setShared] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("modified");
  const [promptOpen, setPromptOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isCollaborator = user?.account_type === "collaborator";

  const load = async () => {
    setLoading(true);
    try {
      const [own, sh] = await Promise.all([
        api.get("/scripts").catch(() => ({ data: { scripts: [] } })),
        api.get("/scripts/shared-with-me").catch(() => ({ data: { scripts: [] } })),
      ]);
      setScripts(own.data.scripts || []);
      setShared(sh.data.scripts || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load scripts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createScript = async (title) => {
    setBusy(true);
    try {
      const { data } = await api.post("/scripts", { title: title?.trim() || "Untitled Screenplay" });
      navigate(`/app/scripts/${data.script.id}`);
    } catch (e) {
      const status = e.response?.status;
      if (status === 402) { navigate("/locked"); return; }
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create script");
    } finally {
      setBusy(false);
      setPromptOpen(false);
    }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/scripts/${id}/duplicate`);
      toast.success(`Duplicated as "${data.script.title}"`);
      load();
    } catch (e) {
      const status = e.response?.status;
      if (status === 402) { navigate("/locked"); return; }
      toast.error(formatApiError(e.response?.data?.detail) || "Could not duplicate");
    }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete this screenplay?",
      description: "This permanently removes the script and its characters, notes and beats. This can't be undone.",
      confirmLabel: "Delete script",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/scripts/${id}`);
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? scripts.filter((s) => (s.title || "").toLowerCase().includes(q))
      : scripts.slice();
    const cmp = {
      modified: (a, b) => new Date(b.updated_at) - new Date(a.updated_at),
      created: (a, b) => new Date(b.created_at) - new Date(a.created_at),
      name: (a, b) => (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" }),
    }[sortKey];
    return filtered.sort(cmp);
  }, [scripts, query, sortKey]);

  return (
    <div className="min-h-screen bg-[var(--canvas)]">
      <ImpersonationBanner />
      <TrialBanner />
      <AppHeader />

      <div className="max-w-[1100px] mx-auto px-6 py-10 md:py-16">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-8 gap-4">
          <div>
            <p className="text-[12px] uppercase tracking-[0.22em] text-[var(--ink-muted)] mb-2">{greeting()} {user?.name || user?.email?.split("@")[0]}</p>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight" data-testid="dashboard-title">
              {isCollaborator ? "Shared with you" : "Your scripts"}
            </h1>
          </div>
          {!isCollaborator && (
            <button onClick={() => setPromptOpen(true)} className="btn-brand inline-flex items-center gap-2" data-testid="new-script-btn">
              <Plus size={16} strokeWidth={2} /> New screenplay
            </button>
          )}
        </div>

        {/* Search + sort toolbar */}
        {!isCollaborator && scripts.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-3 mb-8" data-testid="dashboard-toolbar">
            <div className="relative flex-1 max-w-md">
              <Search size={15} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-muted)]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search scripts…"
                data-testid="dashboard-search-input"
                className="w-full bg-white border border-[var(--line)] rounded-md pl-9 pr-3 py-2 text-sm outline-none focus:border-[var(--brand)] transition-colors"
              />
            </div>
            <SortSelect value={sortKey} onChange={setSortKey} />
          </div>
        )}

        {loading ? (
          <p className="text-[var(--ink-muted)]" data-testid="dashboard-loading">Loading…</p>
        ) : (
          <>
            {!isCollaborator && (
              scripts.length === 0 ? (
                <EmptyState onCreate={() => setPromptOpen(true)} />
              ) : visible.length === 0 ? (
                <p className="text-[var(--ink-muted)] py-10 text-center" data-testid="dashboard-no-results">
                  No scripts match “{query}”.
                </p>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="scripts-grid">
                  {visible.map((s) => (
                    <article
                      key={s.id}
                      className="bg-white border border-[var(--line)] rounded-md p-5 hover:shadow-md transition-shadow group relative"
                      data-testid={`script-card-${s.id}`}
                    >
                      <Link to={`/app/scripts/${s.id}`} className="block">
                        <FileText size={20} strokeWidth={1.5} className="text-[var(--brand)] mb-4" />
                        <h3 className="font-display text-xl mb-1 truncate text-[var(--ink-primary)] pr-12" data-testid={`script-title-${s.id}`}>
                          {s.title || "Untitled"}
                        </h3>
                        <p className="text-xs text-[var(--ink-muted)]" data-testid={`script-meta-${s.id}`}>
                          {pageCountLabel(s.elements)} · updated {formatDate(s.updated_at)}
                        </p>
                      </Link>
                      <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => duplicate(s.id)}
                          className="p-1.5 text-[var(--ink-muted)] hover:text-[var(--brand)]"
                          data-testid={`script-duplicate-${s.id}`}
                          title="Duplicate"
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          onClick={() => remove(s.id)}
                          className="p-1.5 text-[var(--ink-muted)] hover:text-[var(--err)]"
                          data-testid={`script-delete-${s.id}`}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )
            )}

            {shared.length > 0 && (
              <section className={isCollaborator ? "" : "mt-14"} data-testid="shared-section">
                {!isCollaborator && <h2 className="font-display text-2xl mb-4 text-[var(--ink-primary)]">Shared with you</h2>}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shared-grid">
                  {shared.map((s) => (<SharedScriptCard key={s.id} s={s} />))}
                </div>
              </section>
            )}

            {isCollaborator && shared.length === 0 && (
              <div className="bg-white border border-dashed border-[var(--line-strong)] rounded-md p-16 text-center" data-testid="collab-empty">
                <h2 className="font-display text-3xl mb-3">Nothing shared yet.</h2>
                <p className="text-[var(--ink-secondary)] max-w-md mx-auto">When a writer shares a screenplay with you, it'll show up right here.</p>
              </div>
            )}
          </>
        )}
      </div>

      <NameScriptDialog
        open={promptOpen}
        busy={busy}
        onCancel={() => setPromptOpen(false)}
        onConfirm={createScript}
      />
    </div>
  );
}

function SortSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = SORTS.find((s) => s.id === value) || SORTS[0];
  return (
    <div className="relative" data-testid="dashboard-sort">
      <button
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        className="inline-flex items-center gap-2 bg-white border border-[var(--line)] rounded-md px-3 py-2 text-sm text-[var(--ink-secondary)] hover:border-[var(--line-strong)] transition-colors"
        data-testid="dashboard-sort-button"
      >
        <span className="text-[var(--ink-muted)]">Sort:</span> {current.label}
        <ChevronDown size={14} strokeWidth={1.5} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-[var(--line)] rounded-md shadow-lg py-1 min-w-[180px] z-30" data-testid="dashboard-sort-menu">
          {SORTS.map((s) => (
            <button
              key={s.id}
              onMouseDown={() => { onChange(s.id); setOpen(false); }}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-[var(--warm-accent)] ${s.id === value ? "text-[var(--brand)] font-medium" : ""}`}
              data-testid={`dashboard-sort-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NameScriptDialog({ open, busy, onCancel, onConfirm }) {
  const [name, setName] = useState("");
  useEffect(() => { if (open) setName(""); }, [open]);

  const submit = (e) => {
    e?.preventDefault();
    onConfirm(name);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
          data-testid="name-script-overlay"
        >
          <div className="absolute inset-0 bg-[#1c1c1c]/45 backdrop-blur-[3px]" onClick={onCancel} />
          <motion.form
            onSubmit={submit}
            className="relative w-full max-w-[440px] bg-[var(--paper)] border border-[var(--line)] rounded-xl shadow-2xl overflow-hidden"
            initial={{ opacity: 0, scale: 0.94, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            data-testid="name-script-dialog"
          >
            <div className="h-1 w-full" style={{ background: "var(--brand)" }} />
            <div className="p-6">
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "var(--brand-soft)", color: "var(--brand)" }}>
                  <FilePlus2 size={20} strokeWidth={1.75} />
                </span>
                <div className="flex-1 min-w-0 pt-0.5">
                  <h2 className="font-display text-xl text-[var(--ink-primary)] leading-snug">Name your screenplay</h2>
                  <p className="text-sm text-[var(--ink-secondary)] mt-1.5 leading-relaxed">Give it a working title — you can change it any time.</p>
                </div>
              </div>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Untitled Screenplay"
                data-testid="name-script-input"
                className="w-full mt-5 bg-white border border-[var(--line)] rounded-md px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)] transition-colors"
              />
              <div className="flex items-center justify-end gap-2.5 mt-6">
                <button type="button" onClick={onCancel} className="px-4 py-2 text-sm rounded-md text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)] transition-colors" data-testid="name-script-cancel">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="px-4 py-2 text-sm rounded-md text-white font-medium transition-transform active:scale-[0.98] disabled:opacity-60"
                  style={{ background: "var(--brand)" }}
                  data-testid="name-script-create"
                >
                  {busy ? "Creating…" : "Create script"}
                </button>
              </div>
            </div>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const ROLE_BADGE = {
  viewer: "View only",
  feedback: "Feedback",
  cowriter: "Co-writer",
};

function SharedScriptCard({ s }) {
  return (
    <Link
      to={`/app/scripts/${s.id}`}
      className="block bg-white border border-[var(--line)] rounded-md p-5 hover:shadow-md transition-shadow"
      data-testid={`shared-card-${s.id}`}
    >
      <div className="flex items-center justify-between mb-4">
        <FileText size={20} strokeWidth={1.5} className="text-[var(--ink-secondary)]" />
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[var(--brand-soft)] text-[var(--brand-hover)]" data-testid={`shared-role-${s.id}`}>
          {ROLE_BADGE[s.shared_role] || s.shared_role}
        </span>
      </div>
      <h3 className="font-display text-xl mb-1 truncate text-[var(--ink-primary)]">{s.title || "Untitled"}</h3>
      <p className="text-xs text-[var(--ink-muted)]">by {s.owner_name} · updated {formatDate(s.updated_at)}</p>
    </Link>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="bg-white border border-dashed border-[var(--line-strong)] rounded-md p-16 text-center" data-testid="dashboard-empty">
      <h2 className="font-display text-3xl mb-3">A blank page is waiting.</h2>
      <p className="text-[var(--ink-secondary)] mb-8 max-w-md mx-auto">Start your first screenplay. Type a scene heading, press Enter, and watch the page format itself.</p>
      <button onClick={onCreate} className="btn-brand" data-testid="dashboard-empty-cta">Start your first screenplay</button>
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "Working late,";
  if (h < 12) return "Morning,";
  if (h < 18) return "Afternoon,";
  return "Evening,";
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
}
